import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const fetchDesigns = createAsyncThunk(
  'designs/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/designs');
      return data.designs || data || [];
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch designs');
    }
  }
);

export const fetchDesignById = createAsyncThunk(
  'designs/fetchById',
  async (designId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/designs/${designId}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch design');
    }
  }
);

export const fetchCalibratedGeometry = createAsyncThunk(
  'designs/fetchCalibratedGeometry',
  async (designId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/designs/${designId}/calibrated_geometry`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message
          || error.message
          || 'Failed to fetch calibrated geometry'
      );
    }
  }
);

export const createDesign = createAsyncThunk(
  'designs/create',
  async ({ productId, name, designData, thumbnailFile }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/designs', {
        design: {
          product_id: productId,
          name,
          design_data: designData,
        },
      });

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create design');
    }
  }
);

export const updateDesign = createAsyncThunk(
  'designs/update',
  async ({ designId, name, designData, version }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/designs/${designId}`, {
        design: {
          name,
          design_data: designData,
          version,
        },
      });

      return data;
    } catch (error) {
      if (error.response?.status === 409) {
        return rejectWithValue({
          code: 'version_conflict',
          message: error.response.data?.message || 'A newer design version exists on the server',
          serverVersion: error.response.data?.server_version,
        });
      }

      if (error.response?.status === 428) {
        return rejectWithValue({
          code: 'version_precondition',
          message: error.response.data?.message || 'Reload the design before saving changes',
          serverVersion: error.response.data?.server_version,
        });
      }

      return rejectWithValue(error.message || 'Failed to update design');
    }
  }
);

export const deleteDesign = createAsyncThunk(
  'designs/delete',
  async (designId, { rejectWithValue }) => {
    try {
      await api.delete(`/designs/${designId}`);
      return designId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete design');
    }
  }
);

export const fetchProduct3DModel = createAsyncThunk(
  'designs/fetchProduct3DModel',
  async (productId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/products/${productId}/product3d`);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch 3D model');
    }
  }
);

export const uploadDesign = createAsyncThunk(
  'designs/upload',
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/designs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to upload design');
    }
  }
);

export const fetchProductMaterials = createAsyncThunk(
  'designs/fetchProductMaterials',
  async (productId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/products/${productId}/product3d/materials`);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch materials');
    }
  }
);

export const exportDesign = createAsyncThunk(
  'designs/export',
  async ({ designId, format }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/designs/${designId}/export`, {
        format: format || 'pdf'
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to export design');
    }
  }
);

export const fetchExportStatus = createAsyncThunk(
  'designs/exportStatus',
  async ({ designId, jobId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/designs/${designId}/export_status/${jobId}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch export status');
    }
  }
);

const initialState = {
  items: [],
  currentDesign: null,
  products: [],
  exportStatus: null,
  calibratedGeometry: null,
  calibratedGeometryDesignId: null,
  calibratedGeometryLoading: false,
  calibratedGeometryError: null,
  loading: false,
  error: null,
};

const designsSlice = createSlice({
  name: 'designs',
  initialState,
  reducers: {
    clearCurrentDesign: (state) => {
      state.currentDesign = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCalibratedGeometry: (state) => {
      state.calibratedGeometry = null;
      state.calibratedGeometryDesignId = null;
      state.calibratedGeometryLoading = false;
      state.calibratedGeometryError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchDesigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDesigns.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchDesigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch By ID
      .addCase(fetchDesignById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDesignById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDesign = action.payload;
      })
      .addCase(fetchDesignById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create
      .addCase(createDesign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDesign.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
        state.currentDesign = action.payload;
      })
      .addCase(createDesign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update
      .addCase(updateDesign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDesign.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(d => d.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.currentDesign = action.payload;
      })
      .addCase(updateDesign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete
      .addCase(deleteDesign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDesign.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(d => d.id !== action.payload);
        if (state.currentDesign?.id === action.payload) {
          state.currentDesign = null;
        }
      })
      .addCase(deleteDesign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Product 3D Model
      .addCase(fetchProduct3DModel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProduct3DModel.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.products.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.products[index] = action.payload;
        } else {
          state.products.push(action.payload);
        }
      })
      .addCase(fetchProduct3DModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Upload Design
      .addCase(uploadDesign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadDesign.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
        state.currentDesign = action.payload;
      })
      .addCase(uploadDesign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Product Materials
      .addCase(fetchProductMaterials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductMaterials.fulfilled, (state, action) => {
        state.loading = false;
        // Store materials in a separate state or in product metadata
        if (state.products.length > 0) {
          const product = state.products.find(p => p.id === action.meta.arg);
          if (product) {
            product.materials = action.payload;
          }
        }
      })
      .addCase(fetchProductMaterials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Export Design
      .addCase(exportDesign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportDesign.fulfilled, (state, action) => {
        state.loading = false;
        state.exportStatus = action.payload;
      })
      .addCase(exportDesign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Export Status
      .addCase(fetchExportStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExportStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.exportStatus = action.payload;
      })
      .addCase(fetchExportStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Private calibrated geometry for the active owner-managed design
      .addCase(fetchCalibratedGeometry.pending, (state, action) => {
        if (state.calibratedGeometryDesignId !== action.meta.arg) {
          state.calibratedGeometry = null;
        }
        state.calibratedGeometryDesignId = action.meta.arg;
        state.calibratedGeometryLoading = true;
        state.calibratedGeometryError = null;
      })
      .addCase(fetchCalibratedGeometry.fulfilled, (state, action) => {
        if (state.calibratedGeometryDesignId !== action.meta.arg) return;

        state.calibratedGeometry = action.payload;
        state.calibratedGeometryLoading = false;
      })
      .addCase(fetchCalibratedGeometry.rejected, (state, action) => {
        if (state.calibratedGeometryDesignId !== action.meta.arg) return;

        state.calibratedGeometry = null;
        state.calibratedGeometryLoading = false;
        state.calibratedGeometryError = action.payload;
      });
  },
});

export const { clearCurrentDesign, clearError, clearCalibratedGeometry } = designsSlice.actions;
export default designsSlice.reducer;
